(() => {
  if (customElements.get('b2h-personalization-preview')) return;

  const imageCache = new Map();

  const loadImage = (src) => {
    if (!src) return Promise.resolve(null);
    if (!imageCache.has(src)) {
      imageCache.set(src, new Promise((resolve) => {
        const image = new Image();
        image.decoding = 'async';
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => resolve(null);
        image.src = src;
      }));
    }
    return imageCache.get(src);
  };

  const drawContained = (context, image, x, y, width, height) => {
    if (!image?.naturalWidth || !image?.naturalHeight) return;
    const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
    const renderedWidth = image.naturalWidth * scale;
    const renderedHeight = image.naturalHeight * scale;
    context.drawImage(
      image,
      x + (width - renderedWidth) / 2,
      y + (height - renderedHeight) / 2,
      renderedWidth,
      renderedHeight
    );
  };

  const fitText = (context, text, maxWidth, startingSize, minimumSize) => {
    let size = startingSize;
    while (size > minimumSize) {
      context.font = `800 ${size}px Georgia, serif`;
      if (context.measureText(text).width <= maxWidth) break;
      size -= 1;
    }
    return size;
  };

  const drawArcText = (context, text, centerX, centerY, radius) => {
    if (!text) return;
    const characters = [...text];
    const maximumArc = 1.55;
    const measuredWidth = context.measureText(text).width;
    const arc = Math.min(maximumArc, Math.max(0.5, measuredWidth / radius));
    const start = Math.PI / 2 + arc / 2;
    const step = characters.length > 1 ? arc / (characters.length - 1) : 0;

    characters.forEach((character, index) => {
      const angle = start - step * index;
      context.save();
      context.translate(centerX + Math.cos(angle) * radius, centerY + Math.sin(angle) * radius);
      context.rotate(angle - Math.PI / 2);
      context.strokeText(character, 0, 0);
      context.fillText(character, 0, 0);
      context.restore();
    });
  };

  class B2hPersonalizationPreview extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      if (!this.canvas) return;
      this.resizeObserver = new ResizeObserver(() => this.render());
      this.resizeObserver.observe(this);
      this.render();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    set recipe(value) {
      this.currentRecipe = value;
      this.render();
    }

    get recipe() {
      if (this.currentRecipe) return this.currentRecipe;
      let layout = {};
      try {
        layout = JSON.parse(this.dataset.layout || '{}');
      } catch {
        layout = {};
      }
      return {
        recipeKey: this.dataset.recipe || 'suncatcher-v1',
        surfaceSrc: this.dataset.surfaceSrc || '',
        backgroundSrc: this.dataset.backgroundSrc || '',
        artworkSrc: this.dataset.artworkSrc || '',
        photoSrc: this.dataset.photoSrc || '',
        photoCrop: this.dataset.photoCrop || 'Face focus',
        photoZoom: Number(this.dataset.photoZoom || 100),
        photoX: Number(this.dataset.photoX || 50),
        photoY: Number(this.dataset.photoY || 50),
        petName: this.dataset.petName || '',
        dates: this.dataset.dates || '',
        moonMessage: this.dataset.moonMessage || '',
        layout
      };
    }

    async render() {
      if (!this.canvas) return;
      const renderId = (this.renderId || 0) + 1;
      this.renderId = renderId;
      this.dataset.rendered = 'false';

      const recipe = this.recipe;
      const [surface, background, artwork, photo] = await Promise.all([
        loadImage(recipe.surfaceSrc),
        loadImage(recipe.backgroundSrc),
        loadImage(recipe.artworkSrc),
        loadImage(recipe.photoSrc)
      ]);
      if (renderId !== this.renderId || !this.isConnected) return;

      const cssSize = Math.max(72, Math.round(this.getBoundingClientRect().width || 240));
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const outputSize = Math.round(cssSize * pixelRatio);
      this.canvas.width = outputSize;
      this.canvas.height = outputSize;
      const context = this.canvas.getContext('2d');
      if (!context) return;
      context.setTransform(outputSize / 400, 0, 0, outputSize / 400, 0, 0);
      context.clearRect(0, 0, 400, 400);
      context.fillStyle = '#e7e1d7';
      context.fillRect(0, 0, 400, 400);

      const isPhotoSuncatcher = recipe.recipeKey === 'photo-suncatcher-v1';
      if (isPhotoSuncatcher) {
        const glass = context.createRadialGradient(154, 120, 24, 200, 200, 176);
        glass.addColorStop(0, 'rgba(255, 252, 245, 0.98)');
        glass.addColorStop(0.62, 'rgba(241, 230, 211, 0.96)');
        glass.addColorStop(1, 'rgba(176, 139, 87, 0.96)');
        context.save();
        context.shadowColor = 'rgba(47, 35, 27, 0.24)';
        context.shadowBlur = 18;
        context.shadowOffsetY = 9;
        context.beginPath();
        context.arc(200, 200, 172, 0, Math.PI * 2);
        context.fillStyle = glass;
        context.fill();
        context.lineWidth = 9;
        context.strokeStyle = '#332822';
        context.stroke();
        context.restore();

        context.save();
        context.beginPath();
        context.arc(200, 200, 160, 0, Math.PI * 2);
        context.clip();
        context.globalAlpha = 0.2;
        context.strokeStyle = '#ffffff';
        context.lineWidth = 2;
        for (let index = 0; index < 7; index += 1) {
          const y = 72 + index * 45;
          context.beginPath();
          context.moveTo(42, y);
          context.bezierCurveTo(118, y - 14, 278, y + 18, 358, y - 3);
          context.stroke();
        }
        context.restore();
      }

      drawContained(context, surface, 0, 0, 400, 400);
      drawContained(context, background, 0, 0, 400, 400);

      const artworkLayout = recipe.layout?.artwork || [60, 44, 48];
      const artworkSize = Number(artworkLayout[2] || 48) * 4;
      const artworkX = Number(artworkLayout[0] || 60) * 4 - artworkSize / 2;
      const artworkY = Number(artworkLayout[1] || 44) * 4 - artworkSize / 2;

      if (photo) {
        const zoom = Math.max(1, Number(recipe.photoZoom || 100) / 100);
        const horizontalPosition = Math.max(0, Math.min(100, Number(recipe.photoX ?? 50)));
        const verticalPosition = Math.max(0, Math.min(100, Number(recipe.photoY ?? 50)));
        context.save();
        context.beginPath();
        context.arc(artworkX + artworkSize / 2, artworkY + artworkSize / 2, artworkSize / 2, 0, Math.PI * 2);
        context.clip();
        if (recipe.photoCrop === 'Full pet') {
          const containScale = Math.min(artworkSize / photo.naturalWidth, artworkSize / photo.naturalHeight) * zoom;
          const renderedWidth = photo.naturalWidth * containScale;
          const renderedHeight = photo.naturalHeight * containScale;
          const overflowX = Math.max(0, renderedWidth - artworkSize);
          const overflowY = Math.max(0, renderedHeight - artworkSize);
          const destinationX = overflowX > 0
            ? artworkX - overflowX * horizontalPosition / 100
            : artworkX + (artworkSize - renderedWidth) / 2;
          const destinationY = overflowY > 0
            ? artworkY - overflowY * verticalPosition / 100
            : artworkY + (artworkSize - renderedHeight) / 2;
          context.drawImage(photo, destinationX, destinationY, renderedWidth, renderedHeight);
        } else {
          const sourceSize = Math.min(photo.naturalWidth, photo.naturalHeight) / zoom;
          const sourceX = (photo.naturalWidth - sourceSize) * horizontalPosition / 100;
          const sourceY = (photo.naturalHeight - sourceSize) * verticalPosition / 100;
          context.drawImage(photo, sourceX, sourceY, sourceSize, sourceSize, artworkX, artworkY, artworkSize, artworkSize);
        }
        context.restore();
      } else {
        // Amazon source assets share the moon's 400x400 coordinate system.
        drawContained(context, artwork, 0, 0, 400, 400);
      }

      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillStyle = '#fff';
      context.strokeStyle = 'rgba(23, 17, 14, 0.82)';
      context.lineWidth = 2;
      context.shadowColor = 'rgba(0, 0, 0, 0.55)';
      context.shadowBlur = 4;

      const nameLayout = recipe.layout?.name || [60, 55, 30, 4];
      const name = String(recipe.petName || '').trim().toUpperCase();
      if (name) {
        const fontSize = fitText(context, name, Number(nameLayout[2] || 30) * 4, Number(nameLayout[3] || 4) * 4, 5.5);
        context.font = isPhotoSuncatcher
          ? `italic 700 ${fontSize}px Georgia, serif`
          : `800 ${fontSize}px Georgia, serif`;
        context.strokeText(name, Number(nameLayout[0] || 60) * 4, Number(nameLayout[1] || 55) * 4);
        context.fillText(name, Number(nameLayout[0] || 60) * 4, Number(nameLayout[1] || 55) * 4);
      }

      const datesLayout = recipe.layout?.dates || [60, 63, 30, 2.5];
      const dates = String(recipe.dates || '').trim();
      if (dates) {
        const fontSize = fitText(context, dates, Number(datesLayout[2] || 30) * 4, Number(datesLayout[3] || 2.5) * 4, 4.5);
        context.font = `800 ${fontSize}px Georgia, serif`;
        context.strokeText(dates, Number(datesLayout[0] || 60) * 4, Number(datesLayout[1] || 63) * 4);
        context.fillText(dates, Number(datesLayout[0] || 60) * 4, Number(datesLayout[1] || 63) * 4);
      }

      const moonMessage = String(recipe.moonMessage || '').trim();
      if (moonMessage) {
        context.shadowBlur = 0;
        context.font = '800 14px Georgia, serif';
        context.lineWidth = 1.8;
        drawArcText(context, moonMessage, 200, 164, 142);
      }

      this.canvas.setAttribute('aria-label', this.dataset.label || `Customization preview${name ? ` for ${name}` : ''}`);
      this.dataset.rendered = 'true';
      this.dispatchEvent(new CustomEvent('b2h:preview-rendered', { bubbles: true }));
    }
  }

  customElements.define('b2h-personalization-preview', B2hPersonalizationPreview);
})();
